import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { SUMMER_MERGE_YEAR, monthLabel } from "@/utils/periods";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Loader2,
  Send,
  FileSignature,
  Check,
  Clock,
  Calendar,
  Save,
  TrendingUp,
  Info,
  Building2,
  Filter,
  Trash2,
  AlertTriangle,
  Plus,
  X,
  AlertCircle,
  RefreshCw,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ObjectivesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [seasonality, setSeasonality] = useState(null);
  const [resetting, setResetting] = useState(false);
  
  // Multi-site management
  const [userConcessions, setUserConcessions] = useState([]);
  const [selectedConcession, setSelectedConcession] = useState(null);
  const hasConcessionSelector = userConcessions.length > 1 || (user?.role === "admin" || user?.role === "hr");
  
  // Selected year and month
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  
  // Status filter for signature
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Editable values for each seller
  const [editValues, setEditValues] = useState({});
  
  // Available brands for Fleet distribution
  const [allBrands, setAllBrands] = useState([]);

  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  const quarterNames = ["T1 (Jan-Mar)", "T2 (Avr-Juin)", "T3 (Juil-Sept)", "T4 (Oct-Déc)"];
  const quarterMonths = {
    1: [1, 2, 3],
    2: [4, 5, 6],
    3: [7, 8, 9],
    4: [10, 11, 12]
  };

  // Check if selected concession is Fleet (quarterly objectives)
  const isFleetConcession = selectedConcession?.is_fleet === true || selectedConcession?.objective_type === "quarterly";

  // Load user concessions on mount
  useEffect(() => {
    const loadUserConcessions = async () => {
      try {
        const res = await api.get("/auth/me");
        const userRole = res.data.role;
        
        // Fetch all concessions from API
        const concessionsRes = await api.get("/concessions");
        const allConcessions = concessionsRes.data;
        
        let concessions = [];
        
        if (userRole === "admin" || userRole === "hr") {
          // Admin/HR see all concessions
          concessions = allConcessions.map(c => ({
            id: c.id,
            name: c.name,
            is_fleet: c.is_fleet || false,
            objective_type: c.objective_type || "monthly"
          }));
        } else {
          // Manager sees only their concessions
          const concessionIds = res.data.concession_ids || [];
          const concessionNames = res.data.concession_names || [];
          
          concessions = concessionIds.map((id, idx) => {
            const fullConcession = allConcessions.find(c => c.id === id);
            return {
              id,
              name: concessionNames[idx] || id,
              is_fleet: fullConcession?.is_fleet || false,
              objective_type: fullConcession?.objective_type || "monthly"
            };
          });
        }
        
        setUserConcessions(concessions);
        
        // Auto-select the first concession if manager
        if (concessions.length > 0) {
          setSelectedConcession(concessions[0]);
        }
      } catch (err) {
        console.error("Error loading user concessions:", err);
      }
    };
    loadUserConcessions();
  }, []);

  // Load all brands for Fleet distribution
  useEffect(() => {
    if (isFleetConcession) {
      api.get("/brands/all").then(res => {
        setAllBrands(res.data.brands || []);
      }).catch(() => {});
    }
  }, [isFleetConcession]);

  // Fetch data when concession or year changes
  useEffect(() => {
    if (selectedConcession || user?.role === "admin" || user?.role === "hr") {
      fetchData();
    }
  }, [selectedYear, selectedConcession]);

  useEffect(() => {
    // Initialize edit values when month or quarter changes
    // But preserve values that haven't been sent for signature yet
    const activeMonth = isFleetConcession && selectedQuarter 
      ? quarterMonths[selectedQuarter][0]  // First month of quarter for Fleet
      : selectedMonth;
    
    if (activeMonth) {
      setEditValues(prevValues => {
        const values = {};
        sellers.forEach(seller => {
          const obj = getSellerObjective(seller.id, activeMonth);
          const prevValue = prevValues[seller.id];
          
          // If we have a previous value that wasn't sent (no objectiveId or status is draft/null)
          // AND the server doesn't have a newer version, keep the previous value
          const serverHasNewerData = obj && (obj.status === 'pending_seller' || obj.status === 'pending_manager' || obj.status === 'signed');
          
          if (prevValue && !serverHasNewerData && !prevValue.status) {
            // Keep the unsaved values
            values[seller.id] = {
              ...prevValue,
              hasObjective: !!obj,
              objectiveId: obj?.id,
              status: obj?.status || null,
              yousign_status: obj?.yousign_status || null,
              yousign_signature_request_id: obj?.yousign_signature_request_id || null
            };
          } else {
            // Use server values - include bonus fields
            values[seller.id] = {
              vn_target: obj?.vn_target || 0,
              vo_target: obj?.vo_target || 0,
              financing_target: obj?.financing_target || 0,
              bonus_enabled: obj?.bonus_enabled || false,
              bonus: obj?.bonus || 300,
              bonus_description: obj?.bonus_description || "",
              brand_distribution: obj?.brand_distribution || [],
              hasObjective: !!obj,
              objectiveId: obj?.id,
              status: obj?.status || null,
              yousign_status: obj?.yousign_status || null,
              yousign_signature_request_id: obj?.yousign_signature_request_id || null
            };
          }
        });
        return values;
      });
    }
  }, [selectedMonth, selectedQuarter, objectives, sellers, isFleetConcession]);

  // Fetch seasonality when month or selected concession changes
  useEffect(() => {
    const fetchSeasonality = async () => {
      if (selectedMonth && selectedConcession?.name) {
        try {
          const response = await api.get(`/seasonality/${encodeURIComponent(selectedConcession.name)}/${selectedMonth}`);
          setSeasonality(response.data);
        } catch (error) {
          console.error("Error fetching seasonality:", error);
          setSeasonality(null);
        }
      } else if (!selectedMonth) {
        setSeasonality(null);
      }
    };
    fetchSeasonality();
  }, [selectedMonth, selectedConcession]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build params for sellers request
      const sellersParams = {};
      if (selectedConcession && user?.role === "manager") {
        sellersParams.concession_id = selectedConcession.id;
      }
      
      const [objectivesRes, sellersRes] = await Promise.all([
        api.get("/objectives", { params: { year: parseInt(selectedYear) } }),
        api.get("/sellers", { params: sellersParams })
      ]);
      setObjectives(objectivesRes.data);
      setSellers(sellersRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getMonthObjectives = (month) => {
    return objectives.filter(obj => obj.month === month);
  };

  // Get sellers that were created before or during the given month
  const getSellersForMonth = (month) => {
    const year = parseInt(selectedYear);
    return sellers.filter(seller => {
      if (!seller.created_at) return true; // If no creation date, include seller
      const createdDate = new Date(seller.created_at);
      const createdYear = createdDate.getFullYear();
      const createdMonth = createdDate.getMonth() + 1; // getMonth() is 0-indexed
      
      // Include seller if created in a previous year, or in the same year but same or earlier month
      if (createdYear < year) return true;
      if (createdYear === year && createdMonth <= month) return true;
      return false;
    });
  };

  const getMonthStatus = (month) => {
    const monthObjs = getMonthObjectives(month);
    const eligibleSellers = getSellersForMonth(month);
    
    if (monthObjs.length === 0) return "empty";
    
    const allSigned = monthObjs.every(obj => obj.status === "signed");
    const somePending = monthObjs.some(obj => obj.status.includes("pending"));
    const someDraft = monthObjs.some(obj => obj.status === "draft");
    
    if (allSigned && monthObjs.length === eligibleSellers.length) return "complete";
    if (allSigned) return "partial-signed";
    if (somePending) return "pending";
    if (someDraft) return "draft";
    return "partial";
  };

  const getSellerObjective = (sellerId, month) => {
    return objectives.find(obj => obj.seller_id === sellerId && obj.month === month);
  };

  const updateEditValue = (sellerId, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [sellerId]: {
        ...prev[sellerId],
        [field]: value
      }
    }));
  };

  const handleSaveObjective = async (sellerId) => {
    const values = editValues[sellerId];
    if (!values) return false;

    setSavingId(sellerId);
    try {
      const seller = sellers.find(s => s.id === sellerId);
      // Use the seller's concession_id, or fallback to selected concession
      const concessionId = seller?.concession_id || selectedConcession?.id || user?.concession_id;
      
      // For Fleet: use first month of quarter, otherwise use selectedMonth
      const month = isFleetConcession && selectedQuarter 
        ? quarterMonths[selectedQuarter][0] 
        : selectedMonth;
      
      const data = {
        seller_id: sellerId,
        concession_id: concessionId,
        month: month,
        year: parseInt(selectedYear),
        vn_target: parseInt(values.vn_target) || 0,
        vo_target: isFleetConcession ? 0 : (parseInt(values.vo_target) || 0),
        financing_target: isFleetConcession ? 0 : (parseFloat(values.financing_target) || 0),
        bonus_enabled: isFleetConcession ? false : (values.bonus_enabled || false),
        bonus: isFleetConcession ? 0 : (values.bonus_enabled ? 300 : 0),
        bonus_description: isFleetConcession ? "" : (values.bonus_enabled ? (values.bonus_description || "") : ""),
        brand_distribution: isFleetConcession ? (values.brand_distribution || []).filter(d => d.percent > 0) : [],
        notes: "",
        is_quarterly: isFleetConcession,
        quarter: isFleetConcession ? selectedQuarter : null
      };

      if (values.hasObjective && values.objectiveId) {
        // Update existing objective
        await api.put(`/objectives/${values.objectiveId}`, data);
        toast.success("Objectif mis à jour");
      } else {
        // Create new objective
        await api.post("/objectives", data);
        toast.success("Objectif créé");
      }
      
      fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleResetSignature = async (sellerId, objectiveId) => {
    if (!objectiveId) return;
    const ok = window.confirm("Cette action va annuler la signature en cours et remettre l'objectif en brouillon. Continuer ?");
    if (!ok) return;
    setSavingId(sellerId);
    try {
      const res = await api.post(`/objectives/${objectiveId}/reset-signature`);
      toast.success(res.data?.message || "Signature réinitialisée");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setSavingId(null);
    }
  };


  const handleSendForSignature = async (sellerId) => {
    const values = editValues[sellerId];
    
    // Validation: empêcher l'envoi si toutes les valeurs sont à zéro
    const vnTarget = parseInt(values?.vn_target) || 0;
    const voTarget = parseInt(values?.vo_target) || 0;
    const financingTarget = parseFloat(values?.financing_target) || 0;
    
    if (isFleetConcession) {
      // Fleet: only VN matters
      if (vnTarget === 0) {
        toast.error("Impossible d'envoyer : l'objectif VN est à zéro.");
        return;
      }
    } else {
      if (vnTarget === 0 && voTarget === 0 && financingTarget === 0) {
        toast.error("Impossible d'envoyer : tous les objectifs sont à zéro. Veuillez saisir au moins un objectif.");
        return;
      }
    }
    
    setSendingId(sellerId);
    
    try {
      if (!values?.objectiveId) {
        // Need to save first
        const saveResult = await handleSaveObjective(sellerId);
        if (!saveResult) {
          toast.error("Erreur lors de la sauvegarde de l'objectif");
          setSendingId(null);
          return;
        }
        
        // Re-fetch to get the new objective ID
        const objectivesRes = await api.get("/objectives", { params: { year: parseInt(selectedYear) } });
        const newObj = objectivesRes.data.find(o => o.seller_id === sellerId && o.month === selectedMonth);
        if (!newObj) {
          toast.error("Erreur: impossible de trouver l'objectif après sauvegarde");
          setSendingId(null);
          return;
        }
        
        await api.post(`/objectives/${newObj.id}/send-for-signature`);
        toast.success("Objectifs envoyés pour signature");
        fetchData();
      } else {
        // Save first if values changed
        await handleSaveObjective(sellerId);
        await api.post(`/objectives/${values.objectiveId}/send-for-signature`);
        toast.success("Objectifs envoyés pour signature");
        fetchData();
      }
    } catch (error) {
      console.error('Erreur envoi signature:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi pour signature");
    } finally {
      setSendingId(null);
    }
  };

  // Réinitialiser tous les objectifs du mois (suppression + annulation Yousign)
  const handleResetMonth = async () => {
    if (!selectedConcession?.id) return;
    // For Fleet: use first month of the selected quarter (objectives are stored under this month)
    const effectiveMonth = isFleetConcession && selectedQuarter
      ? quarterMonths[selectedQuarter][0]
      : selectedMonth;
    if (!effectiveMonth) return;

    setResetting(true);
    try {
      const response = await api.delete(
        `/objectives/bulk/concession/${selectedConcession.id}?month=${effectiveMonth}&year=${parseInt(selectedYear)}`
      );
      
      const { deleted, yousign_cancelled } = response.data;
      
      if (deleted > 0) {
        toast.success(`${deleted} objectif(s) supprimé(s)${yousign_cancelled > 0 ? `, ${yousign_cancelled} signature(s) Yousign annulée(s)` : ''}`);
      } else {
        toast.info("Aucun objectif à supprimer pour ce mois");
      }
      
      // Rafraîchir les données
      fetchData();
      setEditValues({});
    } catch (error) {
      console.error('Erreur reset:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  const getStatusBadge = (status, yousignStatus) => {
    const statusConfig = {
      draft: { label: "Brouillon", className: "bg-gray-100 text-gray-700" },
      pending_seller: { label: "Attente vendeur", className: "bg-amber-100 text-amber-700" },
      pending_manager: { label: "À signer", className: "bg-blue-100 text-blue-700" },
      signed: { label: "Signé", className: "bg-green-100 text-green-700" }
    };
    const config = statusConfig[status] || statusConfig.draft;
    
    // Add Yousign indicator if applicable
    const hasYousign = yousignStatus && yousignStatus !== "unknown";
    
    return (
      <div className="flex items-center gap-1">
        <Badge className={config.className}>{config.label}</Badge>
        {hasYousign && (
          <Badge className="bg-purple-100 text-purple-700 text-xs">
            🔏 Yousign
          </Badge>
        )}
      </div>
    );
  };

  // Calculate totals for selected month (only for eligible sellers)
  const eligibleSellersForMonth = selectedMonth ? getSellersForMonth(selectedMonth) : [];
  const eligibleSellerIds = new Set(eligibleSellersForMonth.map(s => s.id));
  
  const totals = {
    vn: Object.entries(editValues)
      .filter(([id]) => eligibleSellerIds.has(id))
      .reduce((sum, [, v]) => sum + (parseInt(v?.vn_target) || 0), 0),
    vo: Object.entries(editValues)
      .filter(([id]) => eligibleSellerIds.has(id))
      .reduce((sum, [, v]) => sum + (parseInt(v?.vo_target) || 0), 0),
    financing: Object.entries(editValues)
      .filter(([id]) => eligibleSellerIds.has(id))
      .reduce((sum, [, v]) => sum + (parseFloat(v?.financing_target) || 0), 0)
  };

  if (loading) {
    return (
      <Layout title="Objectifs">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestion des objectifs">
      {/* Concession and Year selectors */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Concession selector for multi-site managers */}
          {hasConcessionSelector && (
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gray-500" />
              <label className="font-medium text-gray-700">Site :</label>
              <Select 
                value={selectedConcession?.id || ""} 
                onValueChange={(v) => {
                  const conc = userConcessions.find(c => c.id === v);
                  setSelectedConcession(conc);
                  setSelectedMonth(null);
                  setSelectedQuarter(null);
                }}
              >
                <SelectTrigger className="w-64" data-testid="concession-selector">
                  <SelectValue placeholder="Choisir une concession" />
                </SelectTrigger>
                <SelectContent>
                  {userConcessions.map((conc) => (
                    <SelectItem key={conc.id} value={conc.id}>{conc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <label className="font-medium text-gray-700">Année :</label>
            <Select value={selectedYear} onValueChange={(v) => { setSelectedYear(v); setSelectedMonth(null); }}>
              <SelectTrigger className="w-32" data-testid="year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {(selectedMonth || selectedQuarter) && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { setSelectedMonth(null); setSelectedQuarter(null); }}
              variant="outline"
              data-testid="back-to-periods-btn"
            >
              ← Retour {isFleetConcession ? 'aux trimestres' : 'aux mois'}
            </Button>
            
            {/* Bouton de réinitialisation du mois/trimestre */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  disabled={resetting}
                  data-testid="reset-period-btn"
                >
                  {resetting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Réinitialiser {isFleetConcession ? 'le trimestre' : 'le mois'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Confirmer la réinitialisation
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isFleetConcession ? (
                      <>
                        Cette action va supprimer <strong>tous les objectifs</strong> du {quarterNames[selectedQuarter - 1]} {selectedYear} pour Fleet 
                        et <strong>annuler les signatures Yousign</strong> en attente.
                      </>
                    ) : (
                      <>
                        Cette action va supprimer <strong>tous les objectifs</strong> de {monthLabel(selectedMonth, parseInt(selectedYear), selectedConcession)} {selectedYear} pour cette concession 
                        et <strong>annuler les signatures Yousign</strong> en attente.
                      </>
                    )}
                    <br /><br />
                    Cette action est irréversible.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResetMonth}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Confirmer la suppression
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Current concession indicator for multi-site */}
      {hasConcessionSelector && selectedConcession && !selectedMonth && !selectedQuarter && (
        <div className="bg-gradient-to-r from-gray-50 to-[#212E38]/5 border border-gray-200 rounded-xl p-4 mb-6" data-testid="current-site-banner">
          <div className="flex items-center gap-3">
            <div className={`${isFleetConcession ? 'bg-blue-100' : 'bg-blue-100'} p-2 rounded-lg`}>
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Site sélectionné</p>
              <p className="font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {selectedConcession.name}
                {isFleetConcession && <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-0.5 rounded">FLEET - Trimestriel</span>}
              </p>
            </div>
            <div className="ml-auto">
              <Badge className="bg-blue-100 text-blue-700">
                {sellers.length} vendeur{sellers.length > 1 ? 's' : ''}
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Quarter grid view for Fleet */}
      {isFleetConcession && !selectedQuarter && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="quarters-grid">
          {[1, 2, 3, 4].map((quarter) => {
            const months = quarterMonths[quarter];
            const quarterObjs = objectives.filter(obj => months.includes(obj.month));
            const hasData = quarterObjs.length > 0;
            const allSigned = quarterObjs.length > 0 && quarterObjs.every(obj => obj.status === "signed");
            const somePending = quarterObjs.some(obj => obj.status?.includes("pending"));
            
            let status = "empty";
            if (allSigned && quarterObjs.length === sellers.length) status = "complete";
            else if (allSigned) status = "partial-signed";
            else if (somePending) status = "pending";
            else if (hasData) status = "draft";

            const statusColors = {
              empty: "bg-blue-50 border-blue-200 hover:border-blue-400",
              draft: "bg-gray-50 border-gray-300 hover:border-gray-400",
              pending: "bg-amber-50 border-amber-300 hover:border-amber-400",
              "partial-signed": "bg-blue-50 border-blue-300 hover:border-blue-400",
              complete: "bg-green-50 border-green-300 hover:border-green-400"
            };

            const statusIcons = {
              empty: null,
              draft: <Save className="w-5 h-5 text-gray-400" />,
              pending: <Clock className="w-5 h-5 text-amber-500" />,
              "partial-signed": <Check className="w-5 h-5 text-blue-500" />,
              complete: <Check className="w-5 h-5 text-green-500" />
            };

            return (
              <button
                key={quarter}
                onClick={() => setSelectedQuarter(quarter)}
                className={`p-8 rounded-xl border-2 transition-all duration-200 ${statusColors[status]} hover:shadow-lg`}
                data-testid={`quarter-${quarter}-btn`}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-xl">
                    T{quarter}
                  </div>
                  <span className="font-bold text-[#212E38] text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {quarterNames[quarter - 1]}
                  </span>
                  <div className="flex items-center gap-2">
                    {statusIcons[status]}
                    <span className="text-sm text-gray-500">
                      {quarterObjs.length}/{sellers.length} vendeurs
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Month grid view (for non-Fleet concessions) */}
      {!isFleetConcession && !selectedMonth && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="months-grid">
          {monthNames.map((month, index) => {
            const monthNum = index + 1;
            // Summer merge: from SUMMER_MERGE_YEAR, hide August tile (absorbed into July)
            const isSummerMerged = parseInt(selectedYear) >= SUMMER_MERGE_YEAR && !isFleetConcession;
            if (isSummerMerged && monthNum === 8) return null;
            const label = monthLabel(monthNum, parseInt(selectedYear), selectedConcession);
            const status = getMonthStatus(monthNum);
            const monthObjs = getMonthObjectives(monthNum);
            
            const statusColors = {
              empty: "bg-gray-50 border-gray-200 hover:border-gray-300",
              draft: "bg-gray-50 border-gray-300 hover:border-gray-400",
              pending: "bg-amber-50 border-amber-300 hover:border-amber-400",
              "partial-signed": "bg-blue-50 border-blue-300 hover:border-blue-400",
              complete: "bg-green-50 border-green-300 hover:border-green-400",
              partial: "bg-gray-50 border-gray-300 hover:border-gray-400"
            };

            const statusIcons = {
              empty: null,
              draft: <Save className="w-4 h-4 text-gray-400" />,
              pending: <Clock className="w-4 h-4 text-amber-500" />,
              "partial-signed": <Check className="w-4 h-4 text-blue-500" />,
              complete: <Check className="w-4 h-4 text-green-500" />,
              partial: <Save className="w-4 h-4 text-gray-400" />
            };

            return (
              <button
                key={monthNum}
                onClick={() => setSelectedMonth(monthNum)}
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${statusColors[status]} hover:shadow-md`}
                data-testid={`month-${monthNum}-btn`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Calendar className="w-8 h-8 text-gray-400" />
                  <span className="font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {label}
                  </span>
                  <div className="flex items-center gap-1">
                    {statusIcons[status]}
                    <span className="text-xs text-gray-500">
                      {monthObjs.length}/{getSellersForMonth(monthNum).length} vendeurs
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected quarter detail view for Fleet */}
      {selectedQuarter && isFleetConcession && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 
                className="text-2xl font-bold text-[#212E38]"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                <span className="bg-blue-600 text-white px-3 py-1 rounded mr-3">T{selectedQuarter}</span>
                {quarterNames[selectedQuarter - 1]} {selectedYear}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Objectifs trimestriels Fleet - {sellers.length} vendeur{sellers.length > 1 ? 's' : ''}
              </p>
            </div>
            
            {/* Status filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter-quarter">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="unsigned">Non signé</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="signed">Signé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Annual plan reference notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="fleet-annual-plan-notice">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              <strong>Information :</strong> Merci de vous référer au plan annuel qui reprend l&apos;ensemble des conditions de rémunération et des clés d&apos;entrée.
            </p>
          </div>

          {/* Fleet Quarterly History Card - to help set objectives */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="fleet-quarterly-history">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Historique Fleet - {quarterNames[selectedQuarter - 1]}
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Données des années précédentes pour vous aider à fixer les objectifs du trimestre
                </p>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  {/* Previous year same quarter */}
                  <div className="bg-white/60 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500 mb-1">{quarterNames[selectedQuarter - 1]} {parseInt(selectedYear) - 1}</div>
                    <div className="text-xl font-bold text-blue-600">
                      {objectives.filter(o => o.month === quarterMonths[selectedQuarter][0] && o.year === parseInt(selectedYear) - 1)
                        .reduce((sum, o) => sum + (o.vn_target || 0), 0) || '-'}
                    </div>
                    <div className="text-xs text-gray-600">VN</div>
                  </div>
                  {/* Previous quarter this year */}
                  <div className="bg-white/60 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {selectedQuarter > 1 ? quarterNames[selectedQuarter - 2] : quarterNames[3]} {selectedQuarter > 1 ? selectedYear : parseInt(selectedYear) - 1}
                    </div>
                    <div className="text-xl font-bold text-blue-600">
                      {(() => {
                        const prevQ = selectedQuarter > 1 ? selectedQuarter - 1 : 4;
                        const prevY = selectedQuarter > 1 ? parseInt(selectedYear) : parseInt(selectedYear) - 1;
                        return objectives.filter(o => o.month === quarterMonths[prevQ][0] && o.year === prevY)
                          .reduce((sum, o) => sum + (o.vn_target || 0), 0) || '-';
                      })()}
                    </div>
                    <div className="text-xs text-gray-600">VN (trim. précédent)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sellers table for Fleet quarterly */}
          <div className="card-dashboard overflow-x-auto" data-testid="fleet-objectives-table">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-blue-50 border-b">
                  <th className="text-left p-4 font-semibold text-gray-600 min-w-[200px]">Vendeur Fleet</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-24">VN T{selectedQuarter}</th>
                  <th className="text-left p-4 font-semibold text-gray-600">Répartition par marque</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-32">Statut</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sellers
                  .filter(seller => {
                    // Filter by status if needed
                    const firstMonthOfQuarter = quarterMonths[selectedQuarter][0];
                    const obj = getSellerObjective(seller.id, firstMonthOfQuarter);
                    if (statusFilter === "all") return true;
                    if (statusFilter === "unsigned") return !obj || obj.status === "draft";
                    if (statusFilter === "pending") return obj?.status?.includes("pending");
                    if (statusFilter === "signed") return obj?.status === "signed";
                    return true;
                  })
                  .map((seller) => {
                    const firstMonthOfQuarter = quarterMonths[selectedQuarter][0];
                    const obj = getSellerObjective(seller.id, firstMonthOfQuarter);
                    const values = editValues[seller.id] || {};
                    const isSaving = savingId === seller.id;
                    const isSending = sendingId === seller.id;
                    const isEditable = !values.status || values.status === "draft";

                    return (
                      <tr key={seller.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <div className="font-medium text-gray-900">{seller.name}</div>
                          <div className="text-sm text-gray-500">{seller.email}</div>
                          {seller.brands && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {seller.brands.slice(0, 3).map((brand, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  {brand}
                                </span>
                              ))}
                              {seller.brands.length > 3 && (
                                <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                  +{seller.brands.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          {seller.constraints && !isFleetConcession && (
                            <div className="text-xs text-orange-600 mt-1">{seller.constraints}</div>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <Input
                            type="number"
                            value={values.vn_target || ""}
                            onChange={(e) => updateEditValue(seller.id, "vn_target", e.target.value)}
                            className="w-20 text-center mx-auto"
                            disabled={!isEditable}
                            data-testid={`vn-input-${seller.id}`}
                          />
                        </td>
                        <td className="p-4">
                          {/* Brand distribution - dropdown + add button */}
                          {isEditable ? (
                            <div className="space-y-2" data-testid={`dist-editor-${seller.id}`}>
                              {(values.brand_distribution || []).map((d, idx) => {
                                const vnTotal = parseInt(values.vn_target) || 0;
                                const vol = d.percent > 0 && vnTotal > 0 ? Math.ceil(vnTotal * d.percent / 100) : 0;
                                const usedBrands = (values.brand_distribution || []).map(x => x.brand).filter(b => b !== d.brand);
                                const availableBrands = allBrands.filter(b => !usedBrands.includes(b));
                                return (
                                  <div key={idx} className="flex items-center gap-2" data-testid={`dist-row-${seller.id}-${idx}`}>
                                    <select
                                      value={d.brand}
                                      onChange={(e) => {
                                        setEditValues(prev => {
                                          const sv = { ...prev[seller.id] };
                                          const newDist = [...(sv.brand_distribution || [])];
                                          newDist[idx] = { ...newDist[idx], brand: e.target.value };
                                          return { ...prev, [seller.id]: { ...sv, brand_distribution: newDist } };
                                        });
                                      }}
                                      className="h-8 rounded-md border border-gray-300 bg-white text-xs px-2 w-36 focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                                      data-testid={`dist-select-${seller.id}-${idx}`}
                                    >
                                      <option value="">Marque...</option>
                                      {availableBrands.map(b => (
                                        <option key={b} value={b}>{b}</option>
                                      ))}
                                    </select>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={d.percent || ""}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 0;
                                        setEditValues(prev => {
                                          const sv = { ...prev[seller.id] };
                                          const newDist = [...(sv.brand_distribution || [])];
                                          newDist[idx] = { ...newDist[idx], percent: val };
                                          return { ...prev, [seller.id]: { ...sv, brand_distribution: newDist } };
                                        });
                                      }}
                                      className="w-16 text-center text-xs h-8"
                                      placeholder="%"
                                      data-testid={`dist-pct-${seller.id}-${idx}`}
                                    />
                                    <span className="text-xs text-gray-400 whitespace-nowrap">%</span>
                                    {vol > 0 && (
                                      <span className="text-xs font-bold text-blue-700 whitespace-nowrap">= {vol}</span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditValues(prev => {
                                          const sv = { ...prev[seller.id] };
                                          const newDist = [...(sv.brand_distribution || [])];
                                          newDist.splice(idx, 1);
                                          return { ...prev, [seller.id]: { ...sv, brand_distribution: newDist } };
                                        });
                                      }}
                                      className="text-red-400 hover:text-red-600 transition-colors p-0.5"
                                      data-testid={`dist-remove-${seller.id}-${idx}`}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                );
                              })}
                              {/* Add brand button */}
                              {(values.brand_distribution || []).length < allBrands.length && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const usedBrands = (values.brand_distribution || []).map(d => d.brand);
                                    const nextBrand = allBrands.find(b => !usedBrands.includes(b)) || "";
                                    setEditValues(prev => {
                                      const sv = { ...prev[seller.id] };
                                      const newDist = [...(sv.brand_distribution || []), { brand: nextBrand, percent: 0 }];
                                      return { ...prev, [seller.id]: { ...sv, brand_distribution: newDist } };
                                    });
                                  }}
                                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors font-medium mt-1"
                                  data-testid={`dist-add-${seller.id}`}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Ajouter une marque
                                </button>
                              )}
                              {/* Total indicator */}
                              {(values.brand_distribution || []).length > 0 && (() => {
                                const totalPct = (values.brand_distribution || []).reduce((s, d) => s + (d.percent || 0), 0);
                                return (
                                  <div className={`text-xs font-medium px-2 py-1 rounded inline-flex items-center gap-1 ${totalPct === 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}
                                       data-testid={`dist-total-${seller.id}`}>
                                    {totalPct === 100 ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                    {totalPct}%
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(values.brand_distribution || []).filter(d => d.percent > 0).map((d, idx) => {
                                const vnTotal = parseInt(values.vn_target) || 0;
                                const vol = vnTotal > 0 ? Math.ceil(vnTotal * d.percent / 100) : 0;
                                return (
                                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                                    <strong>{d.brand}</strong> {d.percent}%{vol > 0 && <> = <span className="font-bold">{vol}</span></>}
                                  </span>
                                );
                              })}
                              {(values.brand_distribution || []).filter(d => d.percent > 0).length === 0 && (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {getStatusBadge(values.status, values.yousign_status)}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {isEditable && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSaveObjective(seller.id)}
                                  disabled={isSaving}
                                  data-testid={`save-btn-${seller.id}`}
                                >
                                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                </Button>
                                {values.hasObjective && (
                                  <Button
                                    size="sm"
                                    className="btn-primary"
                                    onClick={() => handleSendForSignature(seller.id)}
                                    disabled={isSending}
                                    data-testid={`send-btn-${seller.id}`}
                                  >
                                    {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                  </Button>
                                )}
                              </>
                            )}
                            {values.status === "pending_manager" && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => navigate(`/signature/${values.objectiveId}`)}
                                data-testid={`sign-btn-${seller.id}`}
                              >
                                <FileSignature className="w-4 h-4 mr-1" />
                                Signer
                              </Button>
                            )}
                            {(values.status === "pending_seller" || values.status === "pending_manager") && (
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-sm"
                                onClick={() => handleResetSignature(seller.id, values.objectiveId)}
                                disabled={savingId === seller.id}
                                title="Annule la signature Yousign en cours et remet l'objectif en brouillon"
                                data-testid={`reset-sig-btn-${seller.id}`}
                              >
                                {savingId === seller.id ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <XCircle className="w-4 h-4 mr-1" />
                                )}
                                Annuler signature
                              </Button>
                            )}
                            {values.status === "signed" && (
                              <Badge className="bg-green-100 text-green-700">
                                <Check className="w-3 h-3 mr-1" />
                                Signé
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Totals for Quarter */}
          <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-3">Totaux T{selectedQuarter} {selectedYear}</h3>
            <div className="bg-white rounded-lg p-3 text-center inline-block">
              <div className="text-2xl font-bold text-blue-600">{totals.vn}</div>
              <div className="text-sm text-gray-600">VN</div>
            </div>
          </div>
        </div>
      )}

      {/* Selected month detail view (non-Fleet) */}
      {selectedMonth && !isFleetConcession && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <h2 
              className="text-2xl font-bold text-[#212E38]"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              {monthLabel(selectedMonth, parseInt(selectedYear), selectedConcession)} {selectedYear}
            </h2>
            
            {/* Status filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="unsigned">Non signé</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="signed">Signé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Seasonality info card */}
          {seasonality && !seasonality.message && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="seasonality-card">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Saisonnalité historique - {seasonality.month_name}
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Basé sur les données des années précédentes pour <strong>{seasonality.concession_name}</strong>
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-600">{seasonality.budget_vn?.toFixed(0) || 0}</div>
                      <div className="text-xs text-gray-600">Budget VN</div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-indigo-600">{seasonality.budget_vo?.toFixed(0) || 0}</div>
                      <div className="text-xs text-gray-600">Budget VO</div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-amber-600">{(seasonality.budget_dfs || 0).toLocaleString('fr-FR')}</div>
                      <div className="text-xs text-gray-600">Budget DFS (€)</div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-emerald-600">{seasonality.seasonality_vn_percent?.toFixed(1) || 0}%</div>
                      <div className="text-xs text-gray-600">% annuel VN</div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-purple-600">{seasonality.seasonality_vo_percent?.toFixed(1) || 0}%</div>
                      <div className="text-xs text-gray-600">% annuel VO</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sellers table with inline editing */}
          <div className="card-dashboard overflow-x-auto" data-testid="sellers-objectives-table">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left p-4 font-semibold text-gray-600 min-w-[180px]">Vendeur</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-20">VN</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-20">VO</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-28">CAE DFS (€)</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-48">Bonus 300€</th>
                  <th className="text-center p-4 font-semibold text-gray-600 w-28">Statut</th>
                  <th className="text-center p-4 font-semibold text-gray-600 min-w-[180px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSellersForMonth(selectedMonth)
                  .filter((seller) => {
                    if (statusFilter === "all") return true;
                    const values = editValues[seller.id] || {};
                    const status = values.status || null;
                    
                    if (statusFilter === "unsigned") {
                      return !status || status === "draft";
                    }
                    if (statusFilter === "pending") {
                      return status === "pending_seller" || status === "pending_manager";
                    }
                    if (statusFilter === "signed") {
                      return status === "signed";
                    }
                    return true;
                  })
                  .map((seller) => {
                  const values = editValues[seller.id] || {};
                  const canEdit = !values.status || values.status === "draft";
                  const canSend = values.status === "draft" || !values.status;
                  const needsManagerSign = values.status === "pending_manager";
                  
                  return (
                    <tr key={seller.id} className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[#212E38]">{seller.name}</p>
                          <p className="text-sm text-gray-500">{seller.email}</p>
                        </div>
                      </td>
                      <td className="text-center p-4">
                        {canEdit ? (
                          <Input
                            type="number"
                            value={values.vn_target || ""}
                            onChange={(e) => updateEditValue(seller.id, "vn_target", e.target.value)}
                            className="w-20 text-center mx-auto"
                            placeholder="0"
                            data-testid={`vn-input-${seller.id}`}
                          />
                        ) : (
                          <span className="text-xl font-bold text-[#212E38]">{values.vn_target}</span>
                        )}
                      </td>
                      <td className="text-center p-4">
                        {canEdit ? (
                          <Input
                            type="number"
                            value={values.vo_target || ""}
                            onChange={(e) => updateEditValue(seller.id, "vo_target", e.target.value)}
                            className="w-20 text-center mx-auto"
                            placeholder="0"
                            data-testid={`vo-input-${seller.id}`}
                          />
                        ) : (
                          <span className="text-xl font-bold text-[#212E38]">{values.vo_target}</span>
                        )}
                      </td>
                      <td className="text-center p-4">
                        {canEdit ? (
                          <Input
                            type="number"
                            value={values.financing_target || ""}
                            onChange={(e) => updateEditValue(seller.id, "financing_target", e.target.value)}
                            className="w-28 text-center mx-auto"
                            placeholder="0"
                            data-testid={`financing-input-${seller.id}`}
                          />
                        ) : (
                          <span className="text-xl font-bold text-[#212E38]">{values.financing_target?.toLocaleString()}€</span>
                        )}
                      </td>
                      <td className="text-center p-4">
                        {canEdit ? (
                          <div className="flex items-center justify-center gap-2">
                            <input
                              type="checkbox"
                              checked={values.bonus_enabled || false}
                              onChange={(e) => updateEditValue(seller.id, "bonus_enabled", e.target.checked)}
                              className="w-5 h-5 rounded border-gray-300 text-[#212E38] focus:ring-[#212E38] cursor-pointer"
                              data-testid={`bonus-checkbox-${seller.id}`}
                            />
                            {values.bonus_enabled && (
                              <Input
                                type="text"
                                value={values.bonus_description || ""}
                                onChange={(e) => updateEditValue(seller.id, "bonus_description", e.target.value)}
                                className="w-32 text-sm"
                                placeholder="Motif..."
                                data-testid={`bonus-desc-input-${seller.id}`}
                              />
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-[#1a2744]">
                            {values.bonus_enabled ? (
                              <>
                                <span className="font-semibold">300€</span>
                                {values.bonus_description && <span className="text-gray-500 ml-1">({values.bonus_description})</span>}
                              </>
                            ) : "-"}
                          </span>
                        )}
                      </td>
                      <td className="text-center p-4">
                        {values.status ? getStatusBadge(values.status, values.yousign_status) : (
                          <Badge className="bg-gray-100 text-gray-500">Non défini</Badge>
                        )}
                      </td>
                      <td className="text-center p-4">
                        <div className="flex items-center justify-center gap-2 flex-nowrap">
                          {canSend && (
                            <Button
                              size="sm"
                              onClick={() => handleSendForSignature(seller.id)}
                              disabled={sendingId === seller.id || savingId === seller.id}
                              className="btn-primary whitespace-nowrap"
                              data-testid={`send-signature-${seller.id}`}
                            >
                              {(sendingId === seller.id || savingId === seller.id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Envoyer
                                </>
                              )}
                            </Button>
                          )}
                          {needsManagerSign && !values.yousign_signature_request_id && (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/sign/${values.objectiveId}`)}
                              className="btn-primary"
                              data-testid={`sign-objective-${seller.id}`}
                            >
                              <FileSignature className="w-4 h-4 mr-1" />
                              Signer
                            </Button>
                          )}
                          {needsManagerSign && values.yousign_signature_request_id && (
                            <>
                              <span className="text-sm text-purple-600 flex items-center gap-1">
                                🔏 Signature via Yousign
                              </span>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-sm ml-2"
                                onClick={() => handleResetSignature(seller.id, values.objectiveId)}
                                disabled={savingId === seller.id}
                                title="Annule la signature Yousign en cours et remet l'objectif en brouillon"
                                data-testid={`cancel-sig-btn-mgr-${seller.id}`}
                              >
                                {savingId === seller.id ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <XCircle className="w-4 h-4 mr-1" />
                                )}
                                Annuler signature
                              </Button>
                            </>
                          )}
                          {values.status === "pending_seller" && (
                            <>
                              <span className="text-sm text-amber-600">En attente du vendeur</span>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-sm ml-2"
                                onClick={() => handleResetSignature(seller.id, values.objectiveId)}
                                disabled={savingId === seller.id}
                                title="Annule la signature Yousign en cours et remet l'objectif en brouillon"
                                data-testid={`cancel-sig-btn-${seller.id}`}
                              >
                                {savingId === seller.id ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <XCircle className="w-4 h-4 mr-1" />
                                )}
                                Annuler signature
                              </Button>
                            </>
                          )}
                          {values.status === "signed" && (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <Check className="w-4 h-4" />
                              Complété
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="card-kpi border-[#E30613]">
              <p className="text-sm text-gray-500 mb-1">Total VN</p>
              <p className="text-3xl font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {totals.vn}
              </p>
              <p className="text-xs text-gray-400 mt-1">véhicules neufs</p>
            </div>
            <div className="card-kpi border-blue-500">
              <p className="text-sm text-gray-500 mb-1">Total VO</p>
              <p className="text-3xl font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {totals.vo}
              </p>
              <p className="text-xs text-gray-400 mt-1">véhicules d'occasion</p>
            </div>
            <div className="card-kpi border-green-500">
              <p className="text-sm text-gray-500 mb-1">Total CAE DFS</p>
              <p className="text-3xl font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {totals.financing.toLocaleString()}€
              </p>
              <p className="text-xs text-gray-400 mt-1">chiffre d'affaires</p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
